import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from 'services/context';
import Button from 'components/Button';
import Container from 'components/Container';
import Input from 'components/Input';
import Timer from 'components/Timer';

import { INPUT_TYPES, FORGOT_MY_PASSWORD_STATUS, ROUTES, ROLES, NOTIFICATION_TYPES } from 'constants/index.js';

import './style.css';
import { useTranslation } from 'services/hooks';

const ForgotMyPassword = () => {
	const { Api, addNotification } = useAppContext();

	const [user, setUser] = useState(null);
	const [isTimerExpired, setIsTimerExpired] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);

	const navigate = useNavigate();
	const firstInputRef = useRef();
	const secondInputRef = useRef();

	const getTranslation = useTranslation();

	const TRANSLATION_KEY = 'forgot_my_password';

	//The content of the page is rendered according to statuses.
	//The statuses are according to this order:
	//Enter Email ---> Enter Code ---> Reset Password ---> Password Changed
	const [pageStatus, setPageStatus] = useState(FORGOT_MY_PASSWORD_STATUS.Enter_Email);

	const pageStatusesContent = useMemo(() => {
		switch (pageStatus) {
			case FORGOT_MY_PASSWORD_STATUS.Enter_Email: {
				return {
					main_title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_email_main_title`),
					sub_title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_email_sub_title`),
					button_title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_email_button`),
					inputs_content: [
						{
							startIcon: 'emailIcon',
							placeHolder: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_email_field`),
							type: INPUT_TYPES.Email.htmlType,
							name: 'email',
							ref: firstInputRef,
						},
					],
				};
			}
			case FORGOT_MY_PASSWORD_STATUS.Enter_Code: {
				return {
					main_title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_code_main_title`),
					sub_title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_code_sub_title`),
					button_title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_code_button`),
					inputs_content: [
						{
							startIcon: 'passwordIcon',
							placeHolder: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_code_field`),
							type: INPUT_TYPES.LettersAndNumbers.htmlType,
							name: 'code',
							ref: firstInputRef,
						},
					],
				};
			}
			case FORGOT_MY_PASSWORD_STATUS.Reset_Password: {
				return {
					main_title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_main_title`),
					sub_title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_sub_title`),
					button_title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_button`),
					inputs_content: [
						{
							startIcon: 'passwordIcon',
							placeHolder: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_field`),
							type: INPUT_TYPES.LettersAndNumbers.htmlType,
							name: 'newPassword',
							ref: firstInputRef,
						},
						{
							startIcon: 'passwordIcon',
							placeHolder: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_confirm_field`),
							type: INPUT_TYPES.LettersAndNumbers.htmlType,
							name: 'confirmPassword',
							ref: secondInputRef,
						},
					],
				};
			}
			case FORGOT_MY_PASSWORD_STATUS.Password_Changed: {
				return {
					main_title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_changed_main_title`),
					sub_title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_changed_sub_title`),
					button_title: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_changed_button`),
					inputs_content: [],
				};
			}
		}
	}, [pageStatus, getTranslation]);

	//Checks if the page is in Enter_Code status.
	const isEnterCodeStatus = useMemo(() => {
		return pageStatus === FORGOT_MY_PASSWORD_STATUS.Enter_Code;
	}, [pageStatus]);

	//This function returns the matching submit function according to the page status.
	const _handleSubmit = useCallback(
		e => {
			e?.preventDefault();
			if (!e.target) return;

			//Reset the error message.
			setErrorMessage(null);

			const form = new FormData(e.target);
			const formData = Object.fromEntries(form.entries());
			Object.entries(formData).forEach(([key]) => (formData[key] = formData[key].trim())); //Remove spaces.

			switch (pageStatus) {
				case FORGOT_MY_PASSWORD_STATUS.Enter_Email: {
					return emailVerification(formData);
				}
				case FORGOT_MY_PASSWORD_STATUS.Enter_Code: {
					return codeVerification(formData);
				}
				case FORGOT_MY_PASSWORD_STATUS.Reset_Password: {
					return resetPassword(formData);
				}
				case FORGOT_MY_PASSWORD_STATUS.Password_Changed: {
					return navigate(`/${ROUTES.LOGIN}`);
				}
			}
		},
		[pageStatus]
	);

	//This function checks the email entered by the customer.
	//If the email exists in Galway DB, a verification code will be sent to the customer by a trigger email.
	const emailVerification = async ({ email }) => {
		try {
			//In case the customer clicks the 'send again' button and there is no email in the formData.
			if (!email && user) email = user?.email;

			email = email.trim(); //remove spaces

			if (!INPUT_TYPES.Email.regex.test(email)) {
				setErrorMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_email_verification_invalid_error_message`));
				return;
			}

			const res = await Api.SendVerificationCode(email);
			if (!res) {
				setErrorMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_email_verification_incorrect_error_message`));
				return;
			}

			//Only students can change their password.
			if (res.user.roleId !== ROLES.Student) {
				setErrorMessage(
					getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_email_verification_role_error_message`)?.replace(
						'[ROLE]',
						ROLES[res.user.roleId]
					)
				);
				return;
			}

			//If the customer clicks on the "send again" button, with a difference of less than 30 seconds from the last time a code was sent to him,
			//or if 2 emails have already been sent to him today, he is defined as not allowed.
			//If the customer is not allowed, another email will not be sent to him and he need to wait until the next day.
			if (!res.isAllow) {
				setErrorMessage(
					getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_email_verification_limit_error_message`)?.replace(
						'CSR_EMAIL',
						res?.csrEmail?.toLowerCase() || 'support@galwayacademy.com'
					)
				);
				return;
			}

			setUser(res.user);
			setPageStatus(FORGOT_MY_PASSWORD_STATUS.Enter_Code);
		} catch (err) {
			console.error(err);
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_verification_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
		}
	};

	//This function verifies the code entered by the customer and checks that it is still valid and has not expired.
	const codeVerification = async ({ code }) => {
		try {
			if (!INPUT_TYPES.LettersAndNumbers.regex.test(code)) {
				setErrorMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_code_verification_invalid_error_message`));
				return;
			}

			const res = await Api.CheckVerificationCode(user?.userId || 0, code);

			if (!res) {
				addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_verification_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
				return;
			}

			//If the customer enters the correct verification code, the isMatch will be true.
			if (!res.isMatch) {
				setErrorMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_code_verification_incorrect_error_message`));
				return;
			}

			//If the customer enters the correct verification code, but after 5 minutes, the isExpired will be true.
			//If the code has expired, the customer cannot use it.
			if (res.isExpired) {
				setErrorMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_code_verification_expired_error_message`));
				return;
			}

			setPageStatus(FORGOT_MY_PASSWORD_STATUS.Reset_Password);
		} catch (err) {
			console.error(err);
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_verification_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
		}
	};

	//This function checks the new password entered by the customer.
	//If the new password is valid and match the confirm password, The customer's password is updated.
	const resetPassword = async ({ newPassword, confirmPassword }) => {
		try {
			if (newPassword.length !== 8 || !(/\d/.test(newPassword) && /[a-zA-Z]/.test(newPassword))) {
				setErrorMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_validation_error_message`));
				return;
			}

			if (newPassword.trim() !== confirmPassword.trim()) {
				setErrorMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_match_error_message`));
				return;
			}

			const res = await Api.UpdateUserPassword(user.userId, newPassword);
			if (!res) {
				addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_verification_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
				return;
			}

			setPageStatus(FORGOT_MY_PASSWORD_STATUS.Password_Changed);
		} catch (err) {
			console.error(err);
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_verification_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
		}
	};

	useEffect(() => {
		if (firstInputRef.current) {
			firstInputRef.current.focus();
			firstInputRef.current.value = ''; //Clearing the input value.
		}
	}, [pageStatusesContent]);

	return (
		<section className="forgot_my_password">
			<Container>
				<div className="main_title">{pageStatusesContent.main_title}</div>
				<div className="sub_title" dangerouslySetInnerHTML={{ __html: pageStatusesContent.sub_title || '' }}></div>
				<form onSubmit={_handleSubmit}>
					{pageStatusesContent.inputs_content.map((input, index) => {
						return (
							<Input
								key={index}
								startIcon={input.startIcon}
								colorIcon="#7e7e7e"
								iconSize="20"
								placeHolder={input.placeHolder}
								type={input.type}
								name={input.name}
								ref={input.ref}
							/>
						);
					})}
					<Button title={pageStatusesContent.button_title} />
				</form>
				{isEnterCodeStatus && (
					<div className="send_again">
						{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_send_again_title`)}
						{isTimerExpired ? (
							<span onClick={emailVerification}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_send_again_button`)}</span>
						) : (
							<div>
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_timer_title`)}
								<Timer onExpiring={() => setIsTimerExpired(true)} initialMinute={0} initialSeconds={59} />
							</div>
						)}
					</div>
				)}
				{errorMessage && <div className="error_message" dangerouslySetInnerHTML={{ __html: errorMessage }}></div>}
			</Container>
		</section>
	);
};

export default ForgotMyPassword;
